// Runtime URL configuration for @raventic/sdk.
//
// RuntimeUrls is the UNION of the SDKs' RuntimeConfig fields (search + rce; the
// assistant's are a subset of search's). applyRuntime() ALWAYS forwards the matching
// subset to search's and rce's configure(); products/assistant.ts forwards the
// assistant's: build-time env defaults, overlaid by the customer's config.runtime.
//
// The defaults come from build-time constants (Vite `define`, analogous to
// analytics' one define) read via the literal `process.env.RAVENTIC_*` keys —
// the exact key text must remain so `define` can string-replace it. Actual
// values are wired into the customer build in step 05 (PLAN.md Open question #2).

import { configure as configureSearch } from "@raventic/sdk-search-v1";
import { configure as configureRce } from "@raventic/sdk-rce2-v1";

// search    RuntimeConfig = { searchBaseApiUrl; chatbotBaseApiUrl; imageBaseUrl; cdnBaseUrl; stylesUrl; stylesUrlNoProduct; bare }
// rce       RuntimeConfig = { rceBaseApiUrl; imageBaseUrl; cdnBaseUrl; stylesUrl; stylesUrlNoProduct; bare }
// assistant RuntimeConfig = { searchBaseApiUrl; chatbotBaseApiUrl; imageBaseUrl; stylesUrl; stylesUrlNoProduct; bare; preflightTimeout }
//           (configured by products/assistant.ts, only when the config defines the modal).
//           `preflightTimeout` (sdk-assistant 1.4, default 10 s) is deliberately NOT forwarded:
//           it is page-global, shared with the dropdown's chat; add it here when a customer
//           needs another value.
export interface RuntimeUrls {
  searchBaseApiUrl?: string;
  chatbotBaseApiUrl?: string;
  imageBaseUrl?: string;
  cdnBaseUrl?: string;
  rceBaseApiUrl?: string;
  // Shadow-DOM stylesheet URLs (iteration 08). Both SDKs' new path-0 injection hook:
  // `getStylesUrl` returns this URL verbatim for every widget's shadow root, so one
  // combined customer file serves all widgets. `stylesUrlNoProduct` is the twin selected
  // per widget by `disableDefaultProductCardStyles`. Forwarded to EVERY SDK (same
  // values; the assistant modal's rules are a part of the same file, iteration 15) so a
  // stray no-product flag can't fall through to the dead env / cdnBaseUrl path. Baked
  // from `RAVENTIC_SHADOW_*` build-env keys by build-customer.ts.
  stylesUrl?: string;
  stylesUrlNoProduct?: string;
  bare?: boolean;
}

// The effective runtime URLs: the build-time `define` constants, with the customer's
// `cfg` shallow-merged over them (cfg wins). Pure. applyRuntime() forwards the result to
// search + rce; boot() also hands it to the assistant (products/assistant.ts), whose
// `configure()` arrives with its activation instead of being imported here (that import
// would bundle the assistant for every customer).
export function resolveRuntime(cfg?: RuntimeUrls): RuntimeUrls {
  // Build-time defaults. The `process.env.RAVENTIC_*` literals must stay verbatim
  // so Vite `define` can replace them (values land in step 05).
  const defaults: RuntimeUrls = {
    searchBaseApiUrl: process.env.RAVENTIC_SEARCH_BASE_API_URL,
    chatbotBaseApiUrl: process.env.RAVENTIC_CHATBOT_BASE_API_URL,
    imageBaseUrl: process.env.RAVENTIC_IMAGE_BASE_URL,
    cdnBaseUrl: process.env.RAVENTIC_SDK_CDN_URL,
    rceBaseApiUrl: process.env.RAVENTIC_RCE_BASE_API_URL,
    stylesUrl: process.env.RAVENTIC_SHADOW_CSS_URL,
    stylesUrlNoProduct: process.env.RAVENTIC_SHADOW_NO_PRODUCT_CSS_URL,
  };
  // Overlay cfg onto defaults, but skip cfg keys whose value is `undefined` so an
  // explicit `undefined` in config.runtime can't clobber a resolved default.
  const merged: RuntimeUrls = { ...defaults };
  if (cfg) {
    for (const key of Object.keys(cfg) as Array<keyof RuntimeUrls>) {
      const value = cfg[key];
      if (value !== undefined) {
        (merged as Record<keyof RuntimeUrls, unknown>)[key] = value;
      }
    }
  }
  return merged;
}

// Configure both SDKs once with the resolved URLs (resolveRuntime), passing each SDK
// ONLY the defined fields of its own RuntimeConfig subset — never `undefined`, so an
// env/config gap can't clobber an SDK's built-in default.
//
// NOTE: library-mode configure() has no env fallback of its own, so if a URL is
// still missing after this merge the SDK's getter throws on first widget
// creation. That throw is caught per-widget (search) / per-carousel (rce), not
// fatal to boot() — see products/search.ts and products/rce.ts.
export function applyRuntime(cfg?: RuntimeUrls): void {
  const merged = resolveRuntime(cfg);

  // search subset — only defined fields (no `undefined` overrides).
  const searchUrls: {
    searchBaseApiUrl?: string;
    chatbotBaseApiUrl?: string;
    imageBaseUrl?: string;
    cdnBaseUrl?: string;
    stylesUrl?: string;
    stylesUrlNoProduct?: string;
    bare?: boolean;
  } = {};
  if (merged.searchBaseApiUrl !== undefined) searchUrls.searchBaseApiUrl = merged.searchBaseApiUrl;
  if (merged.chatbotBaseApiUrl !== undefined) searchUrls.chatbotBaseApiUrl = merged.chatbotBaseApiUrl;
  if (merged.imageBaseUrl !== undefined) searchUrls.imageBaseUrl = merged.imageBaseUrl;
  if (merged.cdnBaseUrl !== undefined) searchUrls.cdnBaseUrl = merged.cdnBaseUrl;
  if (merged.stylesUrl !== undefined) searchUrls.stylesUrl = merged.stylesUrl;
  if (merged.stylesUrlNoProduct !== undefined) searchUrls.stylesUrlNoProduct = merged.stylesUrlNoProduct;
  if (merged.bare !== undefined) searchUrls.bare = merged.bare;
  configureSearch(searchUrls);

  // rce subset — only defined fields.
  const rceUrls: {
    rceBaseApiUrl?: string;
    imageBaseUrl?: string;
    cdnBaseUrl?: string;
    stylesUrl?: string;
    stylesUrlNoProduct?: string;
    bare?: boolean;
  } = {};
  if (merged.rceBaseApiUrl !== undefined) rceUrls.rceBaseApiUrl = merged.rceBaseApiUrl;
  if (merged.imageBaseUrl !== undefined) rceUrls.imageBaseUrl = merged.imageBaseUrl;
  if (merged.cdnBaseUrl !== undefined) rceUrls.cdnBaseUrl = merged.cdnBaseUrl;
  if (merged.stylesUrl !== undefined) rceUrls.stylesUrl = merged.stylesUrl;
  if (merged.stylesUrlNoProduct !== undefined) rceUrls.stylesUrlNoProduct = merged.stylesUrlNoProduct;
  if (merged.bare !== undefined) rceUrls.bare = merged.bare;
  configureRce(rceUrls);
}
